import type { FileRecord } from "@cohesive/api-client";

/**
 * Coarse content-type bucket used by the detail pane to pick a renderer.
 *
 * The detail pane mounts one renderer per record, not a cascade of
 * `if (mime.startsWith(...))` checks — bucketing here keeps the dispatcher
 * a single `switch` and keeps each renderer focused on one medium's
 * loading/error semantics.
 */
export type FileRecordContentKind =
  | "image"
  | "pdf"
  | "video"
  | "audio"
  | "markdown"
  | "html"
  | "text"
  | "office"
  | "spreadsheet"
  | "fallback";

const OFFICE_MIMES = new Set<string>([
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
]);

// `application/vnd.ms-excel` covers more than legacy .xls: Windows browsers
// report plain `.csv` uploads under this mime, so it must land in the
// spreadsheet bucket (native table viewer), never the office/gview path.
const SPREADSHEET_MIMES = new Set<string>([
  "text/csv",
  "text/tab-separated-values",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

const MARKDOWN_MIMES = new Set<string>(["text/markdown", "text/x-markdown"]);

const TEXTUAL_MIMES = new Set<string>([
  "text/plain",
  "application/json",
  "application/xml",
  "text/x-rst",
]);

const HTML_MIMES = new Set<string>(["text/html", "application/xhtml+xml"]);

function endsWithAny(name: string | undefined, exts: string[]): boolean {
  if (!name) return false;
  const lower = name.toLowerCase();
  return exts.some((ext) => lower.endsWith(ext));
}

function fileNameFromPath(path: string | undefined): string {
  if (!path) return "";
  const segments = path.split("/").filter(Boolean);
  return segments[segments.length - 1] ?? path;
}

/**
 * Classify a FileRecord into a renderer bucket.
 *
 * We read both `content_type` and the path extension because some upload
 * paths (paste, drag/drop across browsers) leave `content_type` empty or
 * set it to `application/octet-stream`, yet the filename still carries a
 * clean extension we can fall back on.
 */
export function classifyFileRecord(record: FileRecord): FileRecordContentKind {
  const mime = (record.content_type || "").toLowerCase();
  const name = fileNameFromPath(record.path);

  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf" || endsWithAny(name, [".pdf"])) return "pdf";
  if (MARKDOWN_MIMES.has(mime) || endsWithAny(name, [".md", ".markdown"]))
    return "markdown";
  // Gate HTML before the generic text/* catch so `.html`/`.htm` files
  // route to the iframe viewer instead of being dumped as raw source.
  if (HTML_MIMES.has(mime) || endsWithAny(name, [".html", ".htm"]))
    return "html";
  // Spreadsheets before the text/* catch — CSV/TSV are `text/*` but want
  // the table viewer, not a raw dump.
  if (
    SPREADSHEET_MIMES.has(mime) ||
    endsWithAny(name, [".csv", ".tsv", ".xls", ".xlsx"])
  )
    return "spreadsheet";
  if (mime.startsWith("text/") || TEXTUAL_MIMES.has(mime)) return "text";
  if (OFFICE_MIMES.has(mime)) return "office";

  // No mime — fall back on extension for common office types.
  if (endsWithAny(name, [".doc", ".docx", ".odt"])) return "office";
  if (endsWithAny(name, [".ppt", ".pptx"])) return "office";

  return "fallback";
}

export function getFileRecordName(record: FileRecord): string {
  // A canvas row's path is a placement slot whose leaf is the canvas id; the
  // name lives on the canvas it places.
  if (record.kind === "canvas") {
    return record.canvas?.name ?? "";
  }
  return fileNameFromPath(record.path);
}

/** Filename without extension for export downloads (e.g. `notes` from `notes.md`). */
export function fileRecordDownloadBasename(record: FileRecord): string {
  const name = getFileRecordName(record);
  const i = name.lastIndexOf(".");
  if (i <= 0) return name.trim() || "download";
  return name.slice(0, i).trim() || "download";
}

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
